import { useCallback, useRef } from 'react';
import { useMarriottAIPreviewStore } from '../store';
import {
  callValidateFindHotels,
  logSessionUpdate,
  SessionUpdateParams,
  SessionUpdateReason,
  SessionUpdateResult,
} from '../services/sessionUpdateService';

const DEBOUNCE_MS = 300;

export interface UseSessionUpdateOptions {
  debounceMs?: number;
  enabled?: boolean;
}

export interface UseSessionUpdateReturn {
  triggerSessionUpdate: (reason: SessionUpdateReason) => void;
  triggerSessionUpdateImmediate: (reason: SessionUpdateReason) => Promise<void>;
}

/**
 * Hook to trigger session updates at various touch points in the chat application.
 * Reads search criteria, filter state, and location state from the current conversation's store.
 * Debounces rapid updates to avoid excessive API calls.
 */
export const useSessionUpdate = (options: UseSessionUpdateOptions = {}): UseSessionUpdateReturn => {
  const { debounceMs = DEBOUNCE_MS, enabled = true } = options;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingReasonRef = useRef<SessionUpdateReason | null>(null);

  const getSessionUpdateParams = useCallback((): SessionUpdateParams | null => {
    const state = useMarriottAIPreviewStore.getState();
    const currentChatId = state.currentChatId;

    if (!currentChatId) {
      return null;
    }

    const conversationState = state.getConversationSearchState(currentChatId);
    const viewMode = state.currentState.viewMode;

    return {
      searchCriteria: conversationState?.searchCriteria,
      filterState: conversationState?.filterState,
      locationState: conversationState?.locationState,
      viewMode,
    };
  }, []);

  const executeSessionUpdate = useCallback(
    async (reason: SessionUpdateReason) => {
      if (!enabled) {
        return;
      }

      const params = getSessionUpdateParams();
      if (!params) {
        return;
      }

      logSessionUpdate(reason, params);

      try {
        const result = await callValidateFindHotels(params);
        console.log('[useSessionUpdate] Session update result:', result);
        return result;
      } catch (error) {
        console.error('[useSessionUpdate] Session update error:', error);
        // Silent fail
        return { success: false, error: 'Failed to update session' };
      }
    },
    [enabled, getSessionUpdateParams]
  );

  const triggerSessionUpdate = useCallback(
    (reason: SessionUpdateReason) => {
      if (!enabled) {
        return;
      }

      pendingReasonRef.current = reason;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const reasonToUse = pendingReasonRef.current || reason;
        pendingReasonRef.current = null;
        debounceTimerRef.current = null;
        executeSessionUpdate(reasonToUse);
      }, debounceMs);
    },
    [enabled, debounceMs, executeSessionUpdate]
  );

  const triggerSessionUpdateImmediate = useCallback(
    async (reason: SessionUpdateReason) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingReasonRef.current = null;
      await executeSessionUpdate(reason);
    },
    [executeSessionUpdate]
  );

  return {
    triggerSessionUpdate,
    triggerSessionUpdateImmediate,
  };
};

/**
 * Standalone function to trigger session update without using the hook.
 * Useful for calling from non-component contexts.
 */
export const triggerSessionUpdateStandalone = async (reason: SessionUpdateReason): Promise<SessionUpdateResult> => {
  const state = useMarriottAIPreviewStore.getState();
  const currentChatId = state.currentChatId;

  if (!currentChatId) {
    console.warn('[triggerSessionUpdateStandalone] No current chat, skipping session update');
    return { success: false, error: 'No current chat' };
  }

  const conversationState = state.getConversationSearchState(currentChatId);
  const viewMode = state.currentState.viewMode;

  const params: SessionUpdateParams = {
    searchCriteria: conversationState?.searchCriteria,
    filterState: conversationState?.filterState,
    locationState: conversationState?.locationState,
    viewMode,
  };

  logSessionUpdate(reason, params);

  try {
    const result = await callValidateFindHotels(params);
    return result;
  } catch (error) {
    console.error('[triggerSessionUpdateStandalone] Session update failed:', error);
    return { success: false, error: 'Failed to update session' };
  }
};
